import { Controller } from '@hotwired/stimulus'
import { watch, nextTick } from 'vue'
import { SfLog } from '@app/adapters/logger'
import { useDropdown } from '@app/shared/pinia/dropdown'
import getPiniaInstance from '@app/shared/pinia/instance'
import SFetch from '@app/utils/sfetch'
import useI18n from '@app/shared/vue/composables/use-I18n'
import {
  endedRunRef,
  subscribeToRunState,
} from '@app/insights/run-state-channel'
import type { QueryResultsMessage } from '@app/insights/types'
import { useInsightsPageRun } from '@app/shared/pinia/insights-page-run'
import type { PageQuery } from '@app/shared/pinia/insights-page-run'

// Mirrors Lists::SfListItemComponent's selected-state classes for the
// `background: "default"` variant used by _segments_list.html.slim
// (sf_list_item_component.rb#selected_classes/#background_classes/
// #hover_classes/#text_color_classes). Duplicated here because "selected"
// can only be resolved client-side — see markSelectedSegment.
const UNSELECTED_ROW_CLASSES = [
  'tw-bg-sf-snow-lighter',
  'hover:tw-bg-sf-slate-subtle-2',
  'tw-text-sf-slate',
  'hover:tw-text-sf-slate-dark-1',
]
const SELECTED_ROW_CLASSES = [
  'tw-bg-sf-primary-subtle',
  'tw-text-sf-slate-dark-2',
]

const ROW_SELECTOR = "[data-behavior='segment-item']"

function refsOf(spinner: HTMLElement): string[] {
  return (spinner.dataset.requestRefs || '').split(' ').filter(Boolean)
}

// A row carries the ids the server built its link from, and "All clients"
// carries neither. Both sides state a query as exactly one id, or as neither,
// so one comparison covers every row.
function rowShowsQuery(row: HTMLElement, query: PageQuery): boolean {
  return (
    (row.dataset.segmentId || null) === idString(query.segmentId) &&
    (row.dataset.partnerSegmentId || null) === idString(query.partnerSegmentId)
  )
}

function idString(id: number | null): string | null {
  return id === null ? null : String(id)
}

// Mounted on the recent-queries dropdown menu content, which stays in the DOM
// at all times and is only toggled via v-show inside the sf-ce-dropdown-wrapper
// web component. There's no DOM event to hook into across that boundary, so
// this watches the shared dropdown store directly and focuses the search
// input once this dropdown becomes the open one.
export default class extends Controller {
  static targets = [
    'searchInput',
    'content',
    'runSpinner',
    'runState',
    'spinnerTemplate',
  ]
  static values = { dropdownId: String, quickAccessUrl: String }

  declare readonly searchInputTarget: HTMLInputElement
  declare readonly contentTarget: HTMLElement
  declare readonly runSpinnerTargets: HTMLElement[]
  declare readonly runStateTarget: HTMLElement
  declare readonly hasRunStateTarget: boolean
  declare readonly spinnerTemplateTarget: HTMLTemplateElement
  declare readonly hasSpinnerTemplateTarget: boolean
  declare readonly dropdownIdValue: string
  declare readonly quickAccessUrlValue: string

  private store = useDropdown(getPiniaInstance())
  private stopWatch: (() => void) | null = null
  // The query the page announced. The server cannot spin its row for a cached
  // run or for one started after this fetch.
  private pageRun = useInsightsPageRun(getPiniaInstance())
  private stopPageRunWatch: (() => void) | null = null
  // Fetched once per page load, on the first open, and reused on every
  // subsequent one. Reset naturally on full navigation, since the controller
  // reconnects from scratch.
  private loaded = false
  private loading = false
  private stopRunStateListening: (() => void) | null = null
  // Bumped on every connect and disconnect, so a fetch that resolves across
  // either can tell that its DOM and its cleanup are already gone.
  private connectionGeneration = 0
  private inFlightRequest: AbortController | null = null

  connect(): void {
    this.connectionGeneration += 1
    // Reconnecting on the same element keeps the list injected on the first
    // open, spinners and all, but the page may have changed query, or started
    // or stopped loading, while this controller was not watching.
    this.stopPageRunWatch = watch(
      () => [this.pageRun.running, this.pageRun.query],
      () => this.applyPageStateToRows(),
      { immediate: true }
    )
    // disconnect() also gave up this controller's share of the results
    // channel. Take it back, or the spinners that are left turn for good.
    this.subscribeToRunState()
    this.stopWatch = watch(
      () => this.store.currentDropdownId,
      (currentDropdownId) => {
        if (currentDropdownId !== this.dropdownIdValue) return
        // A list still to be fetched is scrolled by loadSegments instead, once
        // it has rows to scroll.
        const listAlreadyOnScreen = this.loaded
        void this.loadSegments()
        // The dropdown-wrapper's own v-show update happens in a separate Vue
        // app instance, so wait for it to flush before focusing — otherwise
        // the input may still be display:none and silently fail to focus.
        void nextTick(() => {
          this.resetFilter()
          this.searchInputTarget.focus()
          // A list left over from an earlier open keeps whatever that open
          // scrolled it to.
          if (listAlreadyOnScreen) this.scrollCurrentQueryIntoView()
        })
      }
    )
  }

  // Leaves `loaded` false on failure, so the next open retries instead of
  // getting stuck on an error message.
  private async loadSegments(): Promise<void> {
    if (this.loaded || this.loading) return

    const generation = this.connectionGeneration
    this.loading = true
    this.inFlightRequest = new AbortController()
    try {
      const html = await SFetch.html(this.quickAccessUrlValue, {
        signal: this.inFlightRequest.signal,
      })
      // Disconnected while this was in flight: disconnect() has already run its
      // cleanup, so injecting the list here would leak a subscription onto DOM
      // nobody is looking at.
      if (generation !== this.connectionGeneration) return
      if (html === null) return

      this.unsubscribeFromRunState()
      this.contentTarget.innerHTML = html
      this.loaded = true
      this.applyPageStateToRows()
      this.scrollCurrentQueryIntoView()
      this.subscribeToRunState()
      // A keystroke that landed while this fetch was in flight has nothing
      // to filter yet; the freshly-injected list needs the current value
      // re-applied now that it exists, or it shows fully unfiltered.
      if (this.searchInputTarget.value) this.reapplyFilter()
    } catch (err) {
      if (generation !== this.connectionGeneration) return

      SfLog.warn('Error fetching quick access segments', {
        extra: { error: err },
      })
      this.showLoadError()
    } finally {
      this.loading = false
      this.inFlightRequest = null
    }
  }

  // Replaces the spinner _segments.html.slim renders by default. `loaded`
  // stays false (see the comment above), so closing and reopening the
  // dropdown retries — this only makes the failure visible in the meantime.
  private showLoadError(): void {
    const message = document.createElement('div')
    message.className =
      'tw-flex tw-justify-center tw-py-4 tw-text-sf-slate tw-text-sm'
    message.setAttribute('role', 'alert')
    message.textContent = useI18n().t('shared.start_subnav.query_load_error')

    this.contentTarget.replaceChildren(message)
  }

  // The menu content stays mounted (v-show, not v-if) while closed, so a
  // filter typed before closing is still applied when the dropdown reopens.
  // Clear it and re-dispatch keyup — text-filter#filter (bound to this same
  // element) re-shows every item, and the search input's own clear-icon
  // behavior (setup-search-input.ts) only listens for keyup, so an `input`
  // event alone re-filters but leaves the "×" icon visibly stuck on.
  private resetFilter(): void {
    if (!this.searchInputTarget.value) return
    this.searchInputTarget.value = ''
    this.reapplyFilter()
  }

  // Re-runs text-filter#filter (bound to keyup on this same element) against
  // whatever value is currently in the box.
  private reapplyFilter(): void {
    this.searchInputTarget.dispatchEvent(
      new KeyboardEvent('keyup', { bubbles: true })
    )
  }

  // The list and the page's own spinner both follow the query the page
  // announced, so both are redone whenever that changes, and whenever a fresh
  // list arrives.
  private applyPageStateToRows(): void {
    this.markSelectedSegment()
    this.clearPageRunSpinner()
    this.showPageRunSpinner()
  }

  // The quick_access XHR renders this list, so current_page? in the partial
  // never matches.
  private markSelectedSegment(): void {
    const current = this.pageQueryRow()

    this.segmentRows().forEach((row) => {
      if (row === current) {
        row.classList.remove(...UNSELECTED_ROW_CLASSES)
        row.classList.add(...SELECTED_ROW_CLASSES)
        return
      }

      row.classList.remove(...SELECTED_ROW_CLASSES)
      row.classList.add(...UNSELECTED_ROW_CLASSES)
    })
  }

  // The list scrolls, and the query the user is on can sit well below the fold
  // of it. `nearest` leaves a row that is already in view where it is, and
  // scrolls the list rather than the page, which is what the arrow keys in
  // segments_keyboard_nav_controller do as well.
  private scrollCurrentQueryIntoView(): void {
    this.pageQueryRow()?.scrollIntoView({ block: 'nearest' })
  }

  // The row for the query the page is showing. One row can match: each query
  // appears once, and "All clients" is the only row with neither id.
  private pageQueryRow(): HTMLElement | null {
    const query = this.pageRun.query
    if (!query) return null

    return this.segmentRows().find((row) => rowShowsQuery(row, query)) || null
  }

  private segmentRows(): HTMLElement[] {
    return Array.from(
      this.contentTarget.querySelectorAll<HTMLElement>(ROW_SELECTOR)
    )
  }

  // Puts the page's own run on its row, whether or not the server rendered a
  // spinner there: a cached run is finished before the list is fetched, and a
  // fresh one usually starts after it.
  private showPageRunSpinner(): void {
    if (!this.pageRun.running) return

    const row = this.pageQueryRow()
    if (!row) return

    const spinner =
      row.querySelector<HTMLElement>(
        "[data-segments-dropdown-target='runSpinner']"
      ) || this.addSpinner(row)

    if (spinner) spinner.dataset.pageRun = 'true'
  }

  // Takes the page's mark off every row that should no longer carry it: the
  // row the page has just left, and its own row once it stops loading.
  private clearPageRunSpinner(): void {
    const keep = this.pageRun.running ? this.pageQueryRow() : null

    this.runSpinnerTargets.forEach((spinner) => {
      if (!spinner.dataset.pageRun) return
      if (keep && spinner.closest(ROW_SELECTOR) === keep) return

      delete spinner.dataset.pageRun
      this.removeSpinnerIfIdle(spinner)
    })

    if (this.runSpinnerTargets.length === 0) this.unsubscribeFromRunState()
  }

  // Clones the spinner _segments_list.html.slim keeps beside its rows, so the
  // markup has one source, and puts it where that partial puts it: the
  // right-hand slot Lists::SfListItemComponent gives every row.
  private addSpinner(row: HTMLElement): HTMLElement | null {
    if (!this.hasSpinnerTemplateTarget) return null

    const slot = row.lastElementChild
    if (!slot) return null

    const spinner =
      this.spinnerTemplateTarget.content.firstElementChild?.cloneNode(true)
    if (!(spinner instanceof HTMLElement)) return null

    slot.appendChild(spinner)
    return spinner
  }

  // The fetched list carries the channel and the backlog position to listen
  // from, and only when one of its rows has a spinner waiting on a run. Called
  // again on reconnect, so it has to be a no-op while already listening and
  // when every run this list was showing has since ended.
  private subscribeToRunState(): void {
    if (this.stopRunStateListening) return
    if (!this.hasRunStateTarget || this.runSpinnerTargets.length === 0) return

    const channel = this.runStateTarget.dataset.runStateChannel
    const lastId = Number(this.runStateTarget.dataset.runStateLastId)
    if (!channel || !Number.isFinite(lastId)) return

    this.stopRunStateListening = subscribeToRunState(
      channel,
      lastId,
      (message) => this.handleRunStateMessage(message)
    )
  }

  // A run of a query this list shows no spinner for matches nothing below.
  private handleRunStateMessage(message: QueryResultsMessage): void {
    const requestRef = endedRunRef(message)
    if (!requestRef) return

    this.runSpinnerTargets.forEach((spinner) =>
      this.dropRunFromSpinner(spinner, requestRef)
    )

    if (this.runSpinnerTargets.length === 0) this.unsubscribeFromRunState()
  }

  private dropRunFromSpinner(spinner: HTMLElement, requestRef: string): void {
    const refs = refsOf(spinner)
    if (!refs.includes(requestRef)) return

    spinner.dataset.requestRefs = refs
      .filter((ref) => ref !== requestRef)
      .join(' ')
    this.removeSpinnerIfIdle(spinner)
  }

  // A query can have several runs going at once, the page's own among them,
  // and the row shows one spinner for all of them. It goes when the last one
  // ends, not the first.
  private removeSpinnerIfIdle(spinner: HTMLElement): void {
    if (spinner.dataset.pageRun || refsOf(spinner).length > 0) return

    spinner.remove()
  }

  private unsubscribeFromRunState(): void {
    this.stopRunStateListening?.()
    this.stopRunStateListening = null
  }

  disconnect(): void {
    this.connectionGeneration += 1
    this.stopWatch?.()
    this.stopWatch = null
    this.stopPageRunWatch?.()
    this.stopPageRunWatch = null
    this.inFlightRequest?.abort()
    this.unsubscribeFromRunState()
  }
}
